import type { ComputedRef, Ref } from "vue";
import type { ScopeDescriptor } from "@/types/bbel-types";
import type { ServiceOutputSample } from "@/queries/output-samples";

/**
 * Builder-wide BBEL editor scope (#5683), provided by the editing host (BuilderView) and injected by
 * the service-model fields that embed the universal expression editor. Mirrors the connection context:
 * it avoids prop-drilling the scope through ServiceEditor's dynamic `<component>`. Absent in read-only
 * contexts (run-history replay) — fields then fall back to {@link EMPTY_BBEL_SCOPE} (highlighting only,
 * no suggestions).
 */
export type BbelScopeContext = ComputedRef<ScopeDescriptor>;

/** provide/inject key for {@link BbelScopeContext}. */
export const BBEL_SCOPE_KEY = "builderBbelScope";

/** Safe empty descriptor for read-only / no-provider contexts. */
export const EMPTY_BBEL_SCOPE: ScopeDescriptor = { roots: [], functions: [] };

/**
 * The selected service's own latest output sample (#6022), provided by BuilderView so the response-
 * validation rule builder can offer that service's real `self.output.*` / `self.headers.*` shape as
 * field suggestions. Absent (undefined) when the service hasn't produced a sample yet, or in read-only
 * hosts — the builder then falls back to the fixed `self.*` roots.
 */
export type SelfSampleContext = ComputedRef<ServiceOutputSample | undefined>;

/** provide/inject key for {@link SelfSampleContext}. */
export const SELF_SAMPLE_KEY = "builderSelfSample";

/**
 * One BBEL field registered as the smart picker's insert target — the last field the user focused.
 * The picker column (a permanent panel sibling, see ServiceDetailsPanel) routes inserts here; the
 * field keeps its own caret and re-focuses on insert. caretPaths mirrors the member chain at the
 * field's caret so the picker tree follows typing.
 */
export interface BbelInsertTarget {
  insert: (bare: string, cursorBackBy: number) => void;
  caretPaths: Ref<string[]>;
  el: HTMLElement;
}

/**
 * Host contract for the panel-level picker column: fields register on mount (so a cold-opened
 * picker can auto-target the first field) and report themselves as the active insert target on
 * focus; the host renders the picker and routes inserts. Absent when no picker column exists
 * (fields outside the service panel) — fields then have autocomplete only.
 */
export interface BbelPickerHost {
  active: Ref<BbelInsertTarget | null>;
  setActive: (target: BbelInsertTarget) => void;
  /** Mount-order registration; returns the dispose. The first registered field is the cold-open target. */
  register: (target: BbelInsertTarget) => () => void;
}

/** provide/inject key for {@link BbelPickerHost}. */
export const BBEL_PICKER_HOST_KEY = "builderBbelPickerHost";
